import React, { useState } from "react"
import { Link } from "react-router-dom"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "src/components/ui/tabs"
import { cn } from "src/utils/cn"
import { formatINR } from "../constants"
import DaysOverdueBadge from "./DaysOverdueBadge"
import RemindButton from "./RemindButton"
import DemandLetterButton from "./DemandLetterButton"

export default function DuesTable({ overdue = [], upcoming = [], onRefresh }) {
  const [activeTab, setActiveTab] = useState("overdue")

  const rows = activeTab === "overdue" ? overdue : upcoming

  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex justify-start gap-1 rounded-none border-b border-slate-200 bg-transparent px-4 p-0">
          <TabButton value="overdue" active={activeTab === "overdue"} count={overdue.length}>Overdue</TabButton>
          <TabButton value="upcoming" active={activeTab === "upcoming"} count={upcoming.length}>Upcoming</TabButton>
        </TabsList>
      </Tabs>

      {rows.length === 0 ? (
        <div className="px-6 py-10 text-center text-[13px] text-slate-400">
          {activeTab === "overdue"
            ? "No overdue installments. Great job!"
            : "No upcoming installments in the next 30 days."}
        </div>
      ) : (
        <Table className="text-[13px]">
          <TableHeader className="bg-slate-50 text-left text-slate-500">
            <TableRow className="hover:bg-transparent">
              <TableHead className="h-9 font-medium text-slate-500">Customer</TableHead>
              <TableHead className="h-9 font-medium text-slate-500">Project / Unit</TableHead>
              <TableHead className="h-9 text-right font-medium text-slate-500">Amount</TableHead>
              <TableHead className="h-9 text-right font-medium text-slate-500">Balance</TableHead>
              <TableHead className="h-9 font-medium text-slate-500">Due on</TableHead>
              <TableHead className="h-9 font-medium text-slate-500">Status</TableHead>
              <TableHead className="h-9 text-right font-medium text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.installment_id}>
                <TableCell className="py-2.5">
                  <Link
                    to={`/bookings/${row.booking_id}`}
                    className="font-medium text-slate-900 hover:text-blue-600 hover:underline"
                  >
                    {row.customer_name}
                  </Link>
                </TableCell>
                <TableCell className="py-2.5 text-slate-600">
                  {row.project}
                  {row.unit_number && (
                    <span className="ml-1 text-slate-400">/ {row.unit_number}</span>
                  )}
                </TableCell>
                <TableCell className="py-2.5 text-right text-slate-900">{formatINR(row.amount)}</TableCell>
                <TableCell className="py-2.5 text-right font-medium text-slate-900">
                  {formatINR(row.balance)}
                </TableCell>
                <TableCell className="py-2.5 text-slate-600">
                  {row.due_on ? new Date(row.due_on).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                </TableCell>
                <TableCell className="py-2.5">
                  <DaysOverdueBadge days={row.days_overdue} />
                </TableCell>
                <TableCell className="py-2.5 text-right">
                  <div className="flex items-center justify-end gap-1">
                    <RemindButton
                      installmentId={row.installment_id}
                      customerName={row.customer_name}
                      onSuccess={onRefresh}
                    />
                    <DemandLetterButton
                      bookingId={row.booking_id}
                      installmentId={row.installment_id}
                      customerName={row.customer_name}
                    />
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  )
}

function TabButton({ value, active, count, children }) {
  return (
    <TabsTrigger
      value={value}
      className={cn(
        "-mb-px flex items-center gap-1.5 rounded-none border-b-2 bg-transparent px-3 py-3 text-[13px] font-medium shadow-none transition-colors",
        active
          ? "border-slate-900 text-slate-900"
          : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
      )}
    >
      {children}
      {count > 0 && (
        <span
          className={cn(
            "rounded-full px-1.5 py-0.5 text-[10px] font-medium",
            active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"
          )}
        >
          {count}
        </span>
      )}
    </TabsTrigger>
  )
}
